import type { Pinia } from 'pinia'
import { watch } from 'vue'
import { defineNuxtPlugin } from '#imports'

import useCollectionRegistry from '../../collections/CollectionRegistry'

const FALLBACK_TIMEOUT_MS = 30_000

/**
 * Wait for the collection registry to be initialized (shape URL available)
 * before the app proceeds. A 10s timeout previously let the app mount with
 * an uninitialized registry, so WorkspaceChanged deferred queries never ran.
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  if (!import.meta.client) return

  const pinia = nuxtApp.$pinia as Pinia | undefined
  if (!pinia) return

  try {
    const registry = useCollectionRegistry(pinia)
    if (!registry.isInitialized) {
      await new Promise<void>((resolve) => {
        const stop = watch(
          () => registry.isInitialized,
          (v) => {
            if (v) {
              stop()
              resolve()
            }
          },
          { immediate: true },
        )
        setTimeout(() => {
          stop()
          if (!registry.isInitialized) {
            // eslint-disable-next-line no-console
            console.warn(
              '[ReactiveDB plugin] Registry not initialized after %d ms. Set NUXT_PUBLIC_REACTIVE_DB_SHAPE_URL for team/workspace data.',
              FALLBACK_TIMEOUT_MS,
            )
          }
          resolve()
        }, FALLBACK_TIMEOUT_MS)
      })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[ReactiveDB plugin] Failed to initialize collection registry', error)
  }
})
